import * as _ from "lodash";
import { IBusinessObject } from "../PA.Core/PA.Core.IF.BO";
import { IMessageProvider, IProgressProvider, MessageType } from "../PA.Core/PA.Core.IF.Basic";
import { PAdbContext } from "../PA.Core/PAdbContext";
import { IdentityProvider } from "../PA.Core/Basic/Identity";
import { MessageProvider } from "../PA.Core/Basic/Messages";
import { ProgressProvider } from "../PA.Core/Basic/Progress";
import { WB_EventSpendTypeRelationshipRow, WB_EventTypeRow, WB_TPM_EventSpendTypeRow } from "../PA.Core/PAdbSchemaRows";
import { IwbEventType } from "../PA.Core/PAdbSchemaRowsIF";
import { esWB_Event_Spend_Type, esWB_Event_Spend_Type_Relationship, esWB_Event_Type } from "../PA.Core/PAdbSchema";
import { ESDataStore } from "../PA.Core/lib/ESDataStore";
import { ESRecord } from "../PA.Core/lib/ESRecord";
import { CreateOptions, ESRecordResolver } from "../PA.Core/lib/ESRecordResolver";
import { ASync } from "../PA.Core/Basic/ASync";
import { EventTypeBOValidations } from "./EventTypeBO.Validations";
import { EventTypeBOAudit } from "./EventTypeBO.Audit";
import { PAEnvironment } from "../PA.Core/PAEnvironment";

export interface IEventTypeLoadOptions {
    dbContext?: PAdbContext;
}

const EventTypeLoadOptions_default: IEventTypeLoadOptions = {};

export class EventTypeBO implements IBusinessObject {
    //<editor-fold desc = "Properties and Constructor">

    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_EventType: WB_EventTypeRow;

    dctAuditedDeletes: { [id: number]: number };

    constructor(options?: any) {
        this.CreateOptions = options || {};

        if ("dbContext" in this.CreateOptions) this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({ id: 0, name: "New EventType" });

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = undefined;
        this.LoadProgressProvider = new ProgressProvider("Loading", this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_EventType = undefined;

        this.dctAuditedDeletes = {};
    }

    //</editor-fold>

    get WB_EventType(): WB_EventTypeRow {
        return this.drWB_EventType;
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating EventType");

        const internalOnComplete = new Promise<IBusinessObject>((resolve, error) => {
            self.LoadOptions = options || {};
            if ("dbContext" in self.LoadOptions) self.dbContext = self.LoadOptions.dbContext;
            else self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                const sError = "Unable to determine dbContext for new EventType.";
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            this.dbContext
                .createRecordWithKey(esWB_Event_Type)
                .then((newEventTypeRow: WB_EventTypeRow) => {
                    newEventTypeRow.setDefaults();
                    self.drWB_EventType = newEventTypeRow;
                    self.dbContext.add(esWB_Event_Type, newEventTypeRow);
                    self.Identity.setAll(
                        self.WB_EventType.cif.WB_EVTTYPE_ID,
                        self.WB_EventType.cif.TYPE_ID,
                        self.WB_EventType.cif.TYPE_NAME,
                    );
                    self.LoadProgressProvider.setName(
                        "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")",
                    );
                    self.Identity.listenToRecordUpdates(self.WB_EventType, "WB_EVTTYPE_ID", "TYPE_ID", "TYPE_NAME");

                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);
                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });
        });

        self._OnLoadComplete = internalOnComplete;

        return new Promise<IBusinessObject>((resolve, error) => {
            internalOnComplete
                .then((BO) => {
                    return ASync.resolve();
                })
                .then((ignored) => {
                    self._bIsLoaded = true;
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch((err) => {
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });
        });
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {
        const self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + anId + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {
            if (self.bIsLoaded) {
                self.LoadProgressProvider.exception("EventType already loaded.");
                resolve(self); // "EventType already loaded.";
                return;
            }

            self.LoadOptions = options || {};
            _.defaults(self.LoadOptions, EventTypeLoadOptions_default);

            if ("dbContext" in self.LoadOptions) self.dbContext = self.LoadOptions.dbContext;
            else if (_.isEmpty(self.dbContext)) {
                self.dbContext = new PAdbContext({});
            }

            if (_.isEmpty(self.dbContext)) {
                error("Unable to determine dbContext for Event Type.");
                return;
            }

            if ("RecordResolver" in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve();
            } else {
                recordResolver = EventTypeBO.createRecordResolver({ dbContext: self.dbContext });
                recordResolverLoadPromise = EventTypeBO.setupRecordResolver(recordResolver, [anId]);
            }

            recordResolverLoadPromise
                .then(function () {
                    const aEventTypeRow: any = recordResolver.get(esWB_Event_Type, anId);

                    if (_.isEmpty(aEventTypeRow)) {
                        error("Event Type not Found");
                        return;
                    }

                    self.drWB_EventType = aEventTypeRow;
                    self.Identity.setAll(
                        self.WB_EventType.cif.WB_EVTTYPE_ID,
                        self.WB_EventType.cif.TYPE_ID,
                        self.WB_EventType.cif.TYPE_NAME,
                    );
                    self.LoadProgressProvider.setName(
                        "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")",
                    );
                    self.Identity.listenToRecordUpdates(self.WB_EventType, "WB_EVTTYPE_ID", "TYPE_ID", "TYPE_NAME");
                    self._bIsLoaded = true;
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch(function (err: any) {
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(err);
                    error(err);
                    return;
                });
        });

        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    Validate(options?: any): Promise<IBusinessObject> {
        const self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start(
            "Validating " + self.WB_EventType.cif.TYPE_NAME + "(" + self.WB_EventType.cif.TYPE_ID + ")",
        );
        return new Promise<IBusinessObject>((resolve, reject) => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            if (self.dbContext == undefined) {
                const sError = "Unable to determine dbContext for to Validate Event Type.";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            EventTypeBOValidations.ValidateEmptyFields(self)
                .then(() => {
                    //return  next validation
                    // ()then hanadler

                    if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                        self._bIsValidated = false;
                    } else {
                        self._bIsValidated = true;
                    }
                    self.ValidateProgressProvider.finish();
                    resolve(self);
                })
                .catch((err) => {
                    self._bIsValidated = false;
                    self.ValidateProgressProvider.exception(err);
                    resolve(self);
                });
        });
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbEvent = <IwbEventType>self.drWB_EventType;
        this.SaveProgressProvider.start("Saving " + wbEvent.TYPE_NAME + "(" + wbEvent.TYPE_ID + ")");

        return new Promise<IBusinessObject>((resolve, reject) => {
            // Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // -- We could search the tree if the there is any record where store isn't set.

            if (self.dbContext == undefined) {
                self._bIsSaved = false;
                const sError = "Unable to determine dbContext to Save Event Type.";
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return;
            }
            EventTypeBOAudit.EventTypeAuditBO(self); //updating auditlog table before batch save
            self.SaveOptions = options || {};

            let saveFunction: (
                esName: string,
                saveRecords: ESRecord[] | ESRecord,
                options?: any,
            ) => Promise<ESDataStore>;
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            // Add code to do individual BO save for dbContextRows....
            const bDone = false;
            self.Validate({}).then(() => {
                if (!self.bIsValidated) {
                    self._bIsSaved = false;
                    self.SaveProgressProvider.finish();
                    resolve(self);
                    return;
                }
                return saveFunction(esWB_Event_Type, self.drWB_EventType, {});
            });
            saveFunction(esWB_Event_Type, self.drWB_EventType, {})
                .then(() => {
                    const tasks: Promise<any>[] = [];
                    tasks.push(
                        saveFunction(esWB_Event_Spend_Type_Relationship, self.drWB_EventType.WB_EventSpendTypeRows),
                    );
                    return Promise.all(tasks);
                })
                .then((ignored) => {
                    if (bBatch) {
                        self.dbContext.addBatchCompleteListener(() => {
                            self._bIsSaved = true;
                        });
                    } else {
                        self._bIsSaved = true;
                    }
                    self.SaveProgressProvider.finish();
                    resolve(self);
                })
                .catch((err) => {
                    self.SaveProgressProvider.exception(err);
                    resolve(self);
                });
        });
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    //Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbEventType = <IwbEventType>self.drWB_EventType;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbEventType.TYPE_NAME + "(" + wbEventType.WB_EVTTYPE_ID + ")");
        return new Promise<IBusinessObject>((resolve, error) => {
            // 1. Validate if we can delete the EventType

            // 2. If Valid, mark all the records for deletion.
            self.drWB_EventType.markDeleted();

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then(() => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);
                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err);
                        resolve(self);
                    });
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    //------------Spend Type---------------------------------------------------
    //<editor-fold desc = "SpendTypes">
    addEventSpendType(aEvtSTypeId: number): Promise<IBusinessObject> {
        const self = this;

        return new Promise<IBusinessObject>((resolve, error) => {
            let aEventSpendTypeRow: WB_TPM_EventSpendTypeRow = undefined;
            let aEventSpendTypeRelRow: WB_EventSpendTypeRelationshipRow = undefined;

            self.dbContext
                .find(esWB_Event_Spend_Type, aEvtSTypeId)
                .then((aEventSpendTypeRecord: WB_TPM_EventSpendTypeRow) => {
                    if (_.isEmpty(aEventSpendTypeRecord)) {
                        // Bad User Role Id...
                        resolve(self);
                        return;
                    }
                    aEventSpendTypeRow = aEventSpendTypeRecord;

                    // create new records
                    return self.dbContext.createRecordWithKey(esWB_Event_Spend_Type_Relationship);
                })
                .then((aNewRow: WB_EventSpendTypeRelationshipRow) => {
                    aNewRow.setDefaults();
                    aEventSpendTypeRelRow = aNewRow;
                    aEventSpendTypeRelRow.cif.WB_EVTTYPE_ID = self.drWB_EventType.cif.WB_EVTTYPE_ID;
                    aEventSpendTypeRelRow.cif.WB_EVTSTYPE_ID = aEvtSTypeId;
                    aEventSpendTypeRelRow.WB_EventSpendType = aEventSpendTypeRow;
                    self.dbContext.add(esWB_Event_Spend_Type_Relationship, aEventSpendTypeRelRow);
                    resolve(self);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    removeEventSpendType(anEventSpendTypeRId: number): Promise<IBusinessObject> {
        const self = this;

        return new Promise<IBusinessObject>((resolve, error) => {
            _.forEach(
                self.drWB_EventType.WB_EventSpendTypeRows,
                (aEventSpendTypeRow: WB_EventSpendTypeRelationshipRow) => {
                    if (aEventSpendTypeRow.isActive && aEventSpendTypeRow.cif.WB_EVTSTYPR_ID == anEventSpendTypeRId) {
                        aEventSpendTypeRow.markDeleted();
                    }
                },
            );

            resolve(self);
        });
    }
    //</editor-fold>

    //</editor-fold>

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        const opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ("dbContext" in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const wrksp_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {},
        };

        const aRecResolver = new ESRecordResolver(dbContext, wrksp_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
        return new Promise<void>((resolve, error) => {
            recordResolver
                .findAllWithRelationships(esWB_Event_Type, ids)
                .then((result) => {
                    resolve();
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }
}
PAEnvironment.registerBusinessObjectClass(EventTypeBO, "EventTypeBO", "Event Type");
